import React, { useState, useEffect } from "react";
import Input from "../../../shared/components/FormElements/Input";
import Button from "../../../shared/components/FormElements/Button";
import {
	CheckCircleIcon,
	ShieldCheckIcon,
	InformationCircleIcon,
} from "@heroicons/react/20/solid";
import { ArrowRightIcon } from "@heroicons/react/16/solid";
import PromoPiece from "./PromoPiece";
import AddressSelector from "./AddressSelector";

const Step1 = ({
	formikProps,
	isLoading,
	error,
	trialDays,
	coupon,
	isPaymentUpdate,
}) => {
	const { values, errors, touched } = formikProps;
	const [completedFields, setCompletedFields] = useState(new Set());

	// Track completed fields
	useEffect(() => {
		const completed = new Set();
		if (values.firstName) completed.add("firstName");
		if (values.lastName) completed.add("lastName");
		if (values.email && !errors.email) completed.add("email");
		if (values.practiceName) completed.add("practiceName");
		setCompletedFields(completed);
	}, [values, errors]);

	const formProgress = (completedFields.size / 4) * 100;

	return (
		<div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-fade-in">
			<div className="lg:col-span-1 flex items-center justify-center">
				<PromoPiece trialDays={trialDays} />
			</div>
			<div className="lg:col-span-1 space-y-6 bg-white border-slate-200 border rounded-2xl shadow-sm p-8 transform transition-all duration-500 hover:shadow">
				{error && (
					<div className="bg-red-50 border border-red-200 rounded-lg p-4 animate-shake">
						<div className="text-red-800">
							Er is een fout opgetreden. Probeer het opnieuw.
						</div>
					</div>
				)}

				{isPaymentUpdate && (
					<div className="bg-blue-50/80 border border-blue-100 rounded-xl p-4 animate-slide-down shadow-sm backdrop-blur-sm">
						<div className="flex items-start gap-3">
							<InformationCircleIcon className="size-5 text-blue-600 mt-0.5 flex-shrink-0" />
							<div className="text-blue-900 text-sm leading-relaxed">
								<p className="font-semibold mb-1 text-blue-950">
									Je wijzigt je betaalgegevens
								</p>
								<p className="text-blue-800/90">
									Je wordt doorgestuurd naar iDeal om een
									rekeningsverificatie te doen die voor
									toekomstige betalingen wordt gebruikt.
								</p>
							</div>
						</div>
					</div>
				)}
				{/* Progress bar */}
				{formProgress > 0 && (
					<div className=" animate-slide-up">
						{/* <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
							<span>Voortgang</span>
							<span>{Math.round(formProgress)}% voltooid</span>
						</div> */}
						<div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
							<div
								className="bg-gradient-to-r from-green-500 to-emerald-500 h-1.5 rounded-full transition-all duration-700 ease-out"
								style={{ width: `${formProgress}%` }}
							></div>
						</div>
					</div>
				)}

				{coupon && (
					<div className="animate-slide-up">
						<div className="inline-flex items-center px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-xs font-medium text-emerald-800">
							Coupon <span className="mx-1 font-semibold">{coupon}</span> toegepast
						</div>
					</div>
				)}

				<div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-slide-up">
					<div className="relative transform transition-all duration-300">
						<Input
							name="firstName"
							label="Voornaam"
							type="text"
							width="full"
							className="transition-all duration-200"
						/>
						{completedFields.has("firstName") && (
							<CheckCircleIcon className="size-5 text-green-500 absolute top-2 right-2 animate-scale-in" />
						)}
					</div>

					<div className="relative transform transition-all duration-300">
						<Input
							name="lastName"
							label="Achternaam"
							type="text"
							width="full"
							className="transition-all duration-200"
						/>
						{completedFields.has("lastName") && (
							<CheckCircleIcon className="size-5 text-green-500 absolute top-2 right-2 animate-scale-in" />
						)}
					</div>
				</div>

				<div className="relative transform transition-all duration-300 animate-slide-up">
					<Input
						name="email"
						label="E-mailadres"
						type="email"
						width="full"
						className="transition-all duration-200"
					/>
					{completedFields.has("email") && (
						<CheckCircleIcon className="size-5 text-green-500 absolute top-2 right-2 animate-scale-in" />
					)}
				</div>

				<div className="relative transform transition-all duration-300 animate-slide-up">
					<Input
						name="practiceName"
						label="Praktijknaam"
						type="text"
						width="full"
						className="transition-all duration-200"
					/>
					{completedFields.has("practiceName") && (
						<CheckCircleIcon className="size-5 text-green-500 absolute top-2 right-2 animate-scale-in" />
					)}
				</div>

				<div className="relative transform transition-all duration-300 animate-slide-up">
					<AddressSelector formikProps={formikProps} />
				</div>

				<div className="animate-slide-up">
					<Button
						type="submit"
						size="large"
						fullWidth
						isLoading={isLoading}
						classNames="transform transition-all duration-100 hover:scale-105"
						iconSuffix={ArrowRightIcon}
					>
						{isLoading
							? "Even zoeken naar je praktijk..."
							: "Volgende stap"}
					</Button>
					{/* Show validation errors when form is submitted but invalid */}
					{(errors.firstName ||
						errors.lastName ||
						errors.email ||
						errors.practiceName) &&
						(touched.firstName ||
							touched.lastName ||
							touched.email ||
							touched.practiceName) && (
							<div className="mt-3 bg-orange-50 border border-orange-200 rounded-lg p-3">
								<div className="text-orange-800 text-sm">
									<p className="font-medium mb-1">
										Vul alle vereiste velden in:
									</p>
									<ul className="space-y-1">
										{errors.firstName &&
											touched.firstName && (
												<li>• {errors.firstName}</li>
											)}
										{errors.lastName &&
											touched.lastName && (
												<li>• {errors.lastName}</li>
											)}
										{errors.email && touched.email && (
											<li>• {errors.email}</li>
										)}
										{errors.practiceName &&
											touched.practiceName && (
												<li>• {errors.practiceName}</li>
											)}
									</ul>
								</div>
							</div>
						)}
				</div>

				<div className="flex space-x-5 transform transition-all duration-300 items-center justify-center ">
					<div className="flex items-center gap-2">
						<ShieldCheckIcon className="size-4 text-green-700 flex-shrink-0" />
						<div>
							<p className="text-sm font-medium text-green-800">
								150+ praktijken gingen je voor
							</p>
						</div>
					</div>
				</div>
			</div>

			<style jsx>{`
				@keyframes fade-in {
					from {
						opacity: 0;
						transform: translateY(20px);
					}
					to {
						opacity: 1;
						transform: translateY(0);
					}
				}
				@keyframes slide-up {
					from {
						opacity: 0;
						transform: translateY(30px);
					}
					to {
						opacity: 1;
						transform: translateY(0);
					}
				}
				@keyframes slide-down {
					from {
						opacity: 0;
						transform: translateY(-20px);
					}
					to {
						opacity: 1;
						transform: translateY(0);
					}
				}
				@keyframes scale-in {
					from {
						transform: scale(0);
					}
					to {
						transform: scale(1);
					}
				}
				@keyframes shake {
					0%,
					100% {
						transform: translateX(0);
					}
					25% {
						transform: translateX(-5px);
					}
					75% {
						transform: translateX(5px);
					}
				}
				@keyframes bounce-slow {
					0%,
					100% {
						transform: translateY(0);
					}
					50% {
						transform: translateY(-10px);
					}
				}
				.animate-fade-in {
					animation: fade-in 0.6s ease-out;
				}
				.animate-slide-up {
					animation: slide-up 0.5s ease-out;
				}
				.animate-slide-down {
					animation: slide-down 0.3s ease-out;
				}
				.animate-scale-in {
					animation: scale-in 0.3s ease-out;
				}
				.animate-shake {
					animation: shake 0.5s ease-out;
				}
				.animate-bounce-slow {
					animation: bounce-slow 2s ease-in-out infinite;
				}
			`}</style>
		</div>
	);
};

export default Step1;
